import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import {
  getRedemptionCodeSetProductTypeDisplay,
  RedemptionCodeSet,
  RedemptionCodeSetProductType
} from '../../../../../../../models/redemption-code';
import { RedemptionCodeService } from '../../../../../../../services/redemption-code/redemption-code.service';
import { MembershipService } from '../../../../../../../services/membership/membership.service';
import { EntryPassService } from '../../../../../../../services/entry-pass/entry-pass.service';
import { VoucherPackService } from '../../../../../../../services/voucher-pack/voucher-pack.service';
import { Membership } from '../../../../../../../models/membership';
import { EntryPass } from '../../../../../../../models/entry-pass';
import { VoucherPack, VoucherPackStatus } from '../../../../../../../models/voucher-pack';
import { EntryPassStatus } from '../../../../../../../models/entry-pass-enums';
import { MembershipPurchaseType, MembershipStatus } from '../../../../../../../models/membership-enums';


@Component({
  selector: 'app-create-redemption-code-set-modal',
  templateUrl: './create-redemption-code-set-modal.component.html',
  styleUrls: ['./create-redemption-code-set-modal.component.css']
})
export class CreateRedemptionCodeSetModalComponent implements OnInit {
  @Input() savedCallback: (createdSet: RedemptionCodeSet) => undefined;

  public name = '';
  public productType: RedemptionCodeSetProductType = null;
  public productId: number = null;
  public expirationDate = '';

  public autogenerateCodes = true;
  public numCodes = 100;
  public codeString = '';
  public codeList: string[] = [];

  public memberships: Membership[];
  public entryPasses: EntryPass[];
  public voucherPacks: VoucherPack[];

  public loadingMemberships = true;
  public loadingEntryPasses = true;
  public loadingVoucherPacks = true;
  public loadingSubmit = false;

  public errorMessage = '';
  public serverError: number = null;

  public readonly RedemptionCodeSetProductType = RedemptionCodeSetProductType;
  public readonly MembershipStatus = MembershipStatus;
  public readonly EntryPassStatus = EntryPassStatus;
  public readonly VoucherPackStatus = VoucherPackStatus;
  public readonly getRedemptionCodeSetProductTypeDisplay = getRedemptionCodeSetProductTypeDisplay;
  public readonly Object = Object;

  constructor(
    public activeModal: NgbActiveModal,
    private redemptionCodeService: RedemptionCodeService,
    private membershipService: MembershipService,
    private entryPassService: EntryPassService,
    private voucherPackService: VoucherPackService,
  ) {}

  ngOnInit() {
    this.loadMemberships();
    this.loadEntryPasses();
    this.loadVoucherPacks();
  }

  public submitAvailable() {
    if (this.name.trim() === '') { return false; }
    if (this.productType === null) { return false; }
    if (this.productId === null) { return false; }

    let numCodes = this.numCodes;
    if (!this.autogenerateCodes) {
      numCodes = this.codeList.length;
    }
    if (!numCodes) {
      return false;
    }
    if (numCodes > 1000) {
      return false;
    }

    return true;
  }

  public handleSubmit() {
    if (!this.submitAvailable()) { return; }
    if (this.loadingSubmit) { return; }

    this.loadingSubmit = true;

    let submitPromise: Promise<RedemptionCodeSet>;

    if (this.autogenerateCodes) {
      submitPromise = this.redemptionCodeService
        .createRedemptionCodeSetWithAutogeneratedCodes(
          this.name.trim(),
          this.productType,
          this.productId,
          this.expirationDate,
          this.numCodes
        );
    } else {
      submitPromise = this.redemptionCodeService
        .createRedemptionCodeSetWithCodeList(
          this.name,
          this.productType,
          this.productId,
          this.expirationDate,
          this.codeList
        );
    }

    submitPromise
      .then(createdSet => {
        this.savedCallback(createdSet);
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to create this redemption code set.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public updateCodeList() {
    this.codeList = this.codeString
      .split('\n')
      .map(s => s.trim())
      .filter(s => s !== '');
  }

  private loadMemberships() {
    this.loadingMemberships = true;

    this.membershipService
      .getMemberships(1, 3000, {})
      .then(result => {
        this.memberships = result.results.filter(
          m => {
            return (
              (m.status !== MembershipStatus.retired) &&
              (m.purchase_type === MembershipPurchaseType.prepaid) &&
              !m.is_group
            );
          }
        );
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingMemberships = false; });
  }

  private loadEntryPasses() {
    this.loadingEntryPasses = true;

    this.entryPassService
      .getEntryPasses(1, 3000, {})
      .then(result => {
        this.entryPasses = result.results.filter(
          m => m.status !== EntryPassStatus.retired
        );
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingEntryPasses = false; });
  }

  private loadVoucherPacks() {
    this.loadingVoucherPacks = true;

    this.voucherPackService
      .getVoucherPacks(1, 3000, {})
      .then(result => {
        this.voucherPacks = result.results.filter(
          m => m.status !== VoucherPackStatus.retired
        );
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingVoucherPacks = false; });
  }

}
